<template>
    <div class="lazy-image-gallery" v-if="apiEndpoint">
        <h4 class="section-title">{{ title }}</h4>

        <!-- Initial loading indicator - only show when no images loaded yet -->
        <div class="text-center my-4" v-if="!hasLoaded && images.length === 0">
            <div v-if="loading" class="initial-loading">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2 text-muted">Loading...</p>
            </div>
            <div v-else class="text-muted">
                <i class="fas fa-camera fa-2x mb-2"></i>
                <p>Scroll here to load images</p>
            </div>
        </div>

        <div class="row mb-4" v-if="images.length > 0">
            <div
                class="col-md-4 col-xs-6 thumb"
                v-for="(image, index) in images"
                :key="image.id"
            >
                <a
                    class="thumbnail"
                    href="#"
                    :data-image-id="index + 1"
                    data-toggle="modal"
                    :data-title="image.description"
                    :data-image="image.path"
                    data-target="#image-gallery"
                >
                    <img
                        class="img-thumbnail"
                        :src="image.path"
                        :alt="image.description || 'Gallery image'"
                    >
                </a>
                <!-- Bootstrap responsive clearfix -->
                <div class="clearfix hidden-xs" v-if="(index + 1) % 3 === 0"></div>
                <div class="clearfix visible-xs" v-if="(index + 1) % 2 === 0"></div>
            </div>
        </div>

        <!-- Load more loading indicator -->
        <div class="text-center my-3" v-if="loading && hasLoaded && images.length > 0">
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="sr-only">Loading more images...</span>
            </div>
            <p class="mt-2 text-muted">Loading more images...</p>
        </div>

        <!-- Load more button -->
        <div class="text-center my-3" v-if="hasMore && !loading">
            <button
                class="btn btn-primary"
                @click="loadMore"
                :disabled="loading"
            >
                Show More Images
            </button>
        </div>

        <!-- Error message -->
        <div class="alert alert-warning" v-if="error">
            {{ error }}
        </div>

        <!-- No images message -->
        <div class="alert alert-info" v-if="hasLoaded && !loading && images.length === 0 && !error">
            {{ noImagesMessage }}
        </div>

    </div>
</template>

<script>
export default {
    name: 'LazyImageGallery',
    props: {
        apiEndpoint: {
            type: String,
            required: true
        },
        apiParams: {
            type: Object,
            default: () => ({})
        },
        initialLimit: {
            type: Number,
            default: 6
        },
        loadMoreLimit: {
            type: Number,
            default: 3
        },
        title: {
            type: String,
            default: 'Images'
        },
        noImagesMessage: {
            type: String,
            default: 'No images available.'
        }
    },
    data() {
        return {
            images: [],
            loading: false,
            error: null,
            hasMore: true,
            nextOffset: 0,
            observer: null,
            hasLoaded: false
        };
    },
    async mounted() {
        this.setupIntersectionObserver();
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        async loadImages(isInitial = false) {
            if (this.loading || (!this.hasMore && !isInitial)) {
                return;
            }

            this.loading = true;
            this.error = null;

            try {
                const limit = isInitial ? this.initialLimit : this.loadMoreLimit;
                const response = await axios.get(this.apiEndpoint, {
                    params: {
                        ...this.apiParams,
                        limit: limit,
                        offset: this.nextOffset
                    }
                });

                if (response.data.status === 'success' && response.data.data) {
                    const newImages = response.data.data.data || [];
                    const pagination = response.data.data.pagination || {};

                    if (isInitial) {
                        this.images = newImages;
                        this.hasLoaded = true; // Mark as loaded after successful initial load
                    } else {
                        this.images = [...this.images, ...newImages];
                    }

                    this.hasMore = pagination.hasMore || false;
                    this.nextOffset = pagination.nextOffset || this.images.length;

                    // Update gallery navigation after images are loaded
                    this.updateGalleryNavigation();
                } else {
                    this.error = 'Failed to load images';
                    if (isInitial) {
                        this.hasLoaded = true; // Mark as loaded even on error to prevent retry
                    }
                }
            } catch (error) {
                console.error('Error loading images:', error);
                this.error = 'Failed to load images. Please try again.';
                if (isInitial) {
                    this.hasLoaded = true; // Mark as loaded even on error to prevent retry
                }
            } finally {
                this.loading = false;
            }
        },

        async loadMore() {
            await this.loadImages(false);
        },

        setupIntersectionObserver() {
            if (!('IntersectionObserver' in window)) {
                // Fallback for older browsers - load immediately
                this.loadImages(true);
                return;
            }

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !this.hasLoaded) {
                        this.loadImages(true);
                        this.observer.disconnect();
                    }
                });
            }, {
                root: null,
                rootMargin: '100px',
                threshold: 0.1
            });

            this.observer.observe(this.$el);
        },

        updateGalleryNavigation() {
            // Wait for images to be rendered in DOM
            this.$nextTick(() => {
                // Update data-image-id attributes for existing gallery navigation JavaScript
                const thumbnails = this.$el.querySelectorAll('a.thumbnail');
                thumbnails.forEach((thumbnail, index) => {
                    thumbnail.setAttribute('data-image-id', index + 1);
                });

                // Wait for jQuery and loadGallery function to be available
                this.waitForGalleryFunction(() => {
                    if (typeof window.loadGallery === 'function') {
                        window.loadGallery(true, 'a.thumbnail');
                    }
                });
            });
        },

        waitForGalleryFunction(callback, maxAttempts = 20, attempt = 0) {
            if (attempt >= maxAttempts) {
                console.warn('LazyImageGallery: loadGallery function not available after maximum attempts');
                return;
            }

            // Check if jQuery is loaded and document is ready and loadGallery exists
            if (typeof window.$ !== 'undefined' &&
                typeof window.loadGallery === 'function') {
                callback();
            } else {
                // Wait 50ms and try again
                setTimeout(() => {
                    this.waitForGalleryFunction(callback, maxAttempts, attempt + 1);
                }, 50);
            }
        },

        resetState() {
            this.images = [];
            this.nextOffset = 0;
            this.hasMore = true;
            this.error = null;
            this.hasLoaded = false;

            // Disconnect existing observer and setup new one
            if (this.observer) {
                this.observer.disconnect();
            }
            this.$nextTick(() => {
                this.setupIntersectionObserver();
            });
        }
    },
    watch: {
        apiEndpoint: {
            handler(newEndpoint, oldEndpoint) {
                if (newEndpoint !== oldEndpoint) {
                    // Reset state when endpoint changes
                    this.resetState();
                }
            },
            immediate: false
        },
        apiParams: {
            handler(newParams, oldParams) {
                if (JSON.stringify(newParams) !== JSON.stringify(oldParams)) {
                    // Reset state when params change
                    this.resetState();
                }
            },
            immediate: false,
            deep: true
        }
    }
};
</script>

<style scoped>
.lazy-image-gallery {
    margin: 2rem 0;
}

.thumb {
    margin-bottom: 1rem;
}

.img-thumbnail {
    max-width: 100%;
    height: auto;
    cursor: pointer;
    transition: opacity 0.3s ease;
}

.img-thumbnail:hover {
    opacity: 0.8;
}

.spinner-border {
    width: 2rem;
    height: 2rem;
}


.section-title {
    margin-bottom: 1.5rem;
}

@media (max-width: 768px) {
    .col-xs-6 {
        width: 50%;
        float: left;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Common/LazyImageGallery.vue